import type { RootState } from 'src/redux/store';

import { toast } from 'sonner';
import { useParams } from 'react-router';
import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { removeTenantIdLS } from 'src/utils/localStorageUtils';

import { useTranslate } from 'src/locales';
import { RECAPTCHA_SITE_KEY } from 'src/config';
import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { setChatStateInitial } from 'src/pages/chat/slice/chatSlice';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { resetSettingState } from 'src/pages/setting/slice/settingSlice';
import { setChannelStateInitial } from 'src/pages/channel/slice/channelSlice';

import { AnimateLogoRotate } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';
import { signOut, setSession } from 'src/auth/context/jwt';

import { AuthLoader } from './auth-loader';
import { setAuthStateInitial } from '../slice/authSlice';
import { useLogoutUserMutation, useAutoLoginUserMutation } from '../slice/authApi';

const AutoLogin = () => {
  const { token, email } = useParams<{ token?: string; email?: string }>();
  const [autoLoginUser, { isLoading }] = useAutoLoginUserMutation();
  const [logoutUser, { data: logoutApiResp }] = useLogoutUserMutation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);
  const { handleResetCallDetails } = useCallContext();
  const { checkUserSession } = useAuthContext();

  const [isRecaptchaReady, setRecaptchaReady] = useState(false);
  const [canAttemptLogin, setCanAttemptLogin] = useState(false);
  const [isLoaderLogin, setIsLoaderLogin] = useState(false);

  const handleLoginError = (error: any) => {
    if (error?.data) {
      const { success, message } = error.data;

      if (
        error.originalStatus === 401 ||
        message === 'Tenant is not ACTIVE' ||
        message === 'User is not ACTIVE'
      ) {
        toast.error(t('TEANT_USER_NOT_ACTIVE'));
      } else if (!success && message === 'Email Not Verified') {
        toast.error(t('EMAIL_NOT_VERIFIED'));
      } else if (message === 'Invalid credentials') {
        toast.error(t('INVALID_CREDS'));
      } else if (error.data === 'Tenant Access denied') {
        toast.error('Tenant Access denied');
      } else if (message === 'User not found') {
        toast.error(t('PLS_REGISTER_FIRST'));
      } else if (message === 'Invalid or expired token') {
        toast.error(t('INVALID_OR_EXPIRED_TOKEN'));
      } else {
        toast.error(t('SOMETHING_WENT_WRONG'));
      }
    } else {
      toast.error(t('PLS_TRY_LATER'));
    }

    router.replace(paths.auth.signIn);
  };

  // Wait for recaptcha to be ready
  useEffect(() => {
    if (executeRecaptcha) {
      setRecaptchaReady(true);
    }
  }, [executeRecaptcha]);

  // Trigger login once recaptcha and params are ready
  useEffect(() => {
    const loginWithToken = async () => {
      try {
        if (!token || !email || !executeRecaptcha) {
          router.replace(paths.auth.signIn);
          return;
        }

        // Logout current session if any
        if (
          loginApiData &&
          loginApiData.email &&
          'token' in loginApiData &&
          typeof loginApiData.token === 'string'
        ) {
          await logoutUser({ email: loginApiData?.email }).unwrap();
        }

        const captchaToken = await executeRecaptcha('login');

        if (!captchaToken) {
          toast.error(t('CAPTCHA_VERIFICATION_FAILED'));
          return;
        }

        const response = await autoLoginUser({
          email,
          imPersonateToken: token,
          captchaToken,
        }).unwrap();

        if (response.success) {
          const accessToken = response?.data?.user?.token;
          if (!accessToken) throw new Error('Access token missing from response.');

          setSession(accessToken);
          await checkUserSession?.();
          router.replace(paths.dashboard.root);
        } else {
          console.error('Auto-login failed:', response.message || 'Unknown error');
          router.replace(paths.auth.signIn);
        }

        setIsLoaderLogin(false);
      } catch (error: any) {
        handleLoginError(error);
        setIsLoaderLogin(false);
      }
    };

    if (token && email && isRecaptchaReady && !canAttemptLogin) {
      setCanAttemptLogin(true);
      loginWithToken();
      setIsLoaderLogin(true);
    }
  }, [token, email, isRecaptchaReady]);

  // Clear state after logout
  const clearState = useCallback(async () => {
    await signOut(); // Reset Session
    dispatch(setAuthStateInitial());
    dispatch(setChatStateInitial());
    dispatch(setChannelStateInitial());
    dispatch(resetSettingState());
    handleResetCallDetails();
    removeTenantIdLS();
    await checkUserSession?.();
  }, []);

  useEffect(() => {
    if (logoutApiResp?.success && logoutApiResp.message === 'Logout successful') {
      clearState();
    }
  }, [logoutApiResp]);

  return (
    <div style={{ textAlign: 'center' }}>
      <AnimateLogoRotate sx={{ mb: 3, mx: 'auto' }} />
      {(isLoading || isLoaderLogin || !isRecaptchaReady) && <AuthLoader />}
      <h2>{`${t('PLEASE_WAIT')} ${t('LOGGING_YOU_IN')}`}</h2>
    </div>
  );
};

export function AutoLoginView() {
  const { currentLang } = useTranslate('translation');
  const siteKey = RECAPTCHA_SITE_KEY;

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey} language={currentLang.value || 'en'}>
      <AutoLogin />
    </GoogleReCaptchaProvider>
  );
}
